import { useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { PriceModifiers } from "@repo/utils";
import { getPriceModifiersPath } from "@repo/structure";
import { ColumnFactory, RowContainer } from "./components";
import { object } from "prop-types";
import {
  ElementChatPopup,
  PriceModifierManager,
  useEstimateEditorSettings, useEstimateEditorStore,
  useEstimateStructure,
  VisibilityButton
} from "@tools";
import { Alert, TooltipIcon } from "@components";
import { Trash } from "@assets";

export const PriceModifierRow = observer(({
  priceModifier
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition, useProfitability, showChatThread } = useEstimateEditorSettings();
  
  const { id, name, type, percent, hide, turnOff } = priceModifier;
  
  const [ removeAlertVisible, showRemoveAlert ] = useState(false);
  
  const commentPath = useMemo(() => getPriceModifiersPath(id), [id]);
  const placeholder = useMemo(() => (
    t(`proposal_parts.price_modifiers.${percent > 0 ? "overhead" : "discount"}`)
  ), [ name ]);
  const title = useMemo(() => (
    name || placeholder
  ), [ name, placeholder ]);
  
  const handleTurnOff = () =>
    priceModifier.setTurnOffState(!turnOff);
  const handleVisibility = () =>
    priceModifier.setHide(!hide);
  const handleNameChange = (value) =>
    priceModifier.setName(value);
  const handleRemove = (e) => {
    e.stopPropagation();
    showRemoveAlert(true);
  }
  
  const removePriceModifier = () => {
    structure.removePriceModifier(id);
  }
  
  return (
    <RowContainer
      title={title}
      inputPlaceholder={placeholder}
      onTurnOffChange={handleTurnOff}
      onVisibilityChange={handleVisibility}
      onNameChange={handleNameChange}
      turnOff={turnOff}
      useDragHandleCell={allowEdition && !useProfitability}
      actions={ <>
        {
          showChatThread &&
          <ElementChatPopup
            rowThread
            path={ commentPath }
            taskName={title}
          />
        }
        {
          allowEdition &&
          <PriceModifierManager
            priceModifier={priceModifier}
          />
        }
        {
          visibilityMode &&
          <VisibilityButton
            hidden={hide}
            onChange={handleVisibility}
            inRow
          />
        }
        {
          allowEdition &&
          <TooltipIcon
            icon={<Trash color="error" />}
            onClick={handleRemove}
            className="button-remove noDrag hoverIcon opq"
          >
            {t(`views.editor.dialogs.price_modifiers.remove.${type === PriceModifiers.DISCOUNT ? "discount" : "overhead"}`)}
          </TooltipIcon>
        }
      </> }
    >
      <ColumnFactory type="price_modifier" data={priceModifier} />
      {
        removeAlertVisible &&
        <Alert
          isOpen
          title={t(`views.editor.dialogs.price_modifiers.remove_confirm.${type === PriceModifiers.DISCOUNT ? "discount" : "overhead"}`)}
          acceptText={t("common.yes")}
          onAccept={removePriceModifier}
          onCancel={() => showRemoveAlert(false)}
        />
      }
    </RowContainer>
  );
});

PriceModifierRow.propTypes = {
  priceModifier: object.isRequired,
}